import { createEffect, createMemo, createRoot, createSignal } from "solid-js";
import { createSharedSignal } from "./store";
import { Color } from "./Color";
import { debounce } from "./util";

export type Theme = 'default' | 'light' | 'dark' | 'dark-monochrome' | 'high-contrast'

export const [theme, setTheme] = createSharedSignal<Theme>('theme', () => 'light')

const match = window.matchMedia('(prefers-color-scheme: dark)');
const [prefersDarkMode, setPrefersDarkMode] = createSignal(match.matches);

match.addEventListener('change', event => {
    setPrefersDarkMode(event.matches);
});

const bodyObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
        const themeAttr = document.body.getAttribute('data-color-theme');
        if (mutation.attributeName === 'data-color-theme' && themeAttr !== theme()) {
            setTheme(themeAttr as Theme);
        }
    }
})
bodyObserver.observe(document.body, { attributes: true })

function highContrastColors(): Color[] {
    if (window.ST?.Preferences) {
        const colors: Color[] = (window.ST.Preferences.HighContrastColor?.split(',') ?? [])
            .filter((c: string) => !!c)
            .map((c: string) => new Color(c));
        if (!colors[0]) { colors.push(new Color('#fff')) };
        if (!colors[1]) { colors.push(colors[0].complement()); }

        return colors;
    }

    return [new Color('#fff'), new Color('#ccc')];
}

const postifx = () => {
    const currentTheme = theme();

    switch (currentTheme) {
        case 'dark':            return '_d';
        case 'dark-monochrome': return '_w';
        case 'light':           return '';
        case 'default':         return prefersDarkMode() ? '_d' : '';
        case 'high-contrast':
            const hcColors = highContrastColors();
            return (hcColors[0].luminosity() < 0.5) ? '_w' : '';
        default:
            // This is a compile-time check to ensure we handle all cases (ensures our switch is exhaustive)
            const _: never = currentTheme;
    }

    return '';
}

createRoot(() => {
    const updateHtmlTheme = debounce((theme: Theme) => {
        // We're using data-color-theme here to avoid any potential collision with the legacy theme attribute data-theme
        document.body.setAttribute('data-color-theme', theme)

        document.body.removeAttribute('data-hc-mode');
        if (theme == 'high-contrast') {
            // TODO: Convert legacy framework to consider luminosity. it currently defaults to light mode for high contrast
            //const hcColors = highContrastColors();
            //const hcMode = (hcColors[0].luminosity() < 0.5) ? 'dark' : 'light';
            const hcMode = 'light';
            document.body.setAttribute('data-hc-mode', hcMode);
        }

        // TODO: Update DX controls
    }, 100);

    if (import.meta.env.DEV) {
        if (localStorage['ST.ColorTheme']) {
            setTheme(localStorage['ST.ColorTheme']);
        }
    }

    createEffect(() => {
        const themeVal = theme();
        if (!document.body.classList.contains('ignore-theme')) {
            updateHtmlTheme(themeVal);
            localStorage['ST.ColorTheme'] = themeVal;
        }
    })
})

export function transformImagePath(path?: string | null) {
    if (!path) return '';

    const upath = path.toUpperCase();
    if (upath
        && upath.includes('DATA:IMAGE') === false
        && upath.startsWith('HTTP') === false
        && upath.startsWith('/') == false
        && upath.startsWith('PHOTOS') === false
        && upath.startsWith('IMAGES/') === false
        && upath.startsWith('GLYPH/') === false
    ) {
        path = `Images/${path}`;
    }

    if (upath.indexOf('ST_VIEW/') !== -1) {
        let qsIdx = path.indexOf('?');
        if (qsIdx === -1) {
            qsIdx = path.length;
        }

        const fnIdx = path.lastIndexOf('/', qsIdx);
        const extIdx = path.lastIndexOf('.', qsIdx);

        const dir = path.substring(0, fnIdx + 1);
        const filename = path.substring(fnIdx + 1, extIdx);
        const extension = path.substring(extIdx);
        const hasPostfix = filename.endsWith('_d') || filename.endsWith('_w');
        const basename = hasPostfix ? filename.substring(0, filename.length - 2) : filename;
        const postfix = postifx();

        path = dir + basename + postfix + extension;
    } else if (path) {
        path = path.replace(new RegExp('Images/AURORA', 'i'), 'Images/ST_AURORA');
        path = path.replace(new RegExp('Images/GENESEA', 'i'), 'Images/ST_GENESEA');
        path = path.replace(new RegExp('Images/GENESIS', 'i'), 'Images/ST_GENESIS');
        path = path.replace(new RegExp('Images/REVELATION', 'i'), 'Images/ST_REVELATION');
    }

    return path ?? '';
}

